import { createStyles, Paper, Text, Anchor, Tooltip, Transition, Rating } from "@mantine/core";
import { Link } from "react-router-dom";
import { getAnimeTitleByRelevance, getImageByRelevance } from "../custom/AnimeData";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { openConfirmModal } from "@mantine/modals";
import { showGenericCheckBoxNotification } from "../custom/Notification";
import { useLanguageStore } from "../store/LanguageToggleStore";
import { useShallow } from "zustand/react/shallow";
import { useWatchListStore } from "../store/WatchListStore";
import { getLastWatched, removeLastWatched } from "../custom/CloudSync";
import { useDisclosure } from "@mantine/hooks";
import { WATCHANIME_RED } from "../constants/cssConstants";

const useStyles = createStyles((theme) => ({
    noTextDecoration: {
        textDecoration: "none",
        "&:hover": {
            textDecoration: "none",
        },
    },
    animeCardHoverState: {
        transitionProperty: "opacity",
        transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
        transitionDuration: ".5s",
        opacity: 0,
        width: "100%",
        height: "100%",
        "&:hover": {
            opacity: "1",
        },
    },
    newCard: {
        width: "270px",
        height: "45vh",
        marginRight: "15px",
        minHeight: "400px",
    },
    playBackTimeDiv: {
        height: "5px",
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "10px",
    },
}));

function Card({ animeData, isDeletable, isAddableToWatchList, featureId, setLastWatchedData, ActionComponent, actionComponentData }) {
    const { classes } = useStyles();
    const { language } = useLanguageStore(useShallow((state) => ({ language: state.language })));
    const { handleWatchListAdd, handleWatchListDelete } = useWatchListStore(useShallow((state) => ({ handleWatchListAdd: state.handleWatchListAdd, handleWatchListDelete: state.handleWatchListDelete })));
    const [isCardOpen, isCardOpenHandler] = useDisclosure(false);
    setTimeout(() => {
        isCardOpenHandler.open();
    }, 10);

    const handleDeleteFromAnimeCard = (e, featureId, selectedAnimeData) => {
        e.preventDefault();
        if (featureId === "lastWatched") {
            return handleDeleteFromLastWatched(selectedAnimeData);
        }
        if (featureId === "watchList") {
            return handleDeleteFromWatchList(selectedAnimeData);
        }
    };

    const handleDeleteFromWatchList = (selectedAnimeData) => {
        openConfirmModal({
            title: "Please confirm your action",
            children: <Text size="sm">Are you sure you want to delete {getAnimeTitleByRelevance(selectedAnimeData.titles, false, language)} from your watchlist?</Text>,
            labels: { confirm: "Confirm", cancel: "Cancel" },
            confirmProps: { color: "red" },
            onCancel: () => {
                //dont do anything
            },
            onConfirm: async () => {
                await handleWatchListDelete(selectedAnimeData);
            },
            centered: true,
        });
    };

    const handleDeleteFromLastWatched = (selectedAnimeData) => {
        openConfirmModal({
            title: "Please confirm your action",
            children: <Text size="sm">Are you sure you want to delete {getAnimeTitleByRelevance(selectedAnimeData.titles, false, language)} from your watch history?</Text>,
            labels: { confirm: "Confirm", cancel: "Cancel" },
            confirmProps: { color: "red" },
            onCancel: () => {
                //dont do anything
            },
            onConfirm: async () => {
                await removeLastWatched(selectedAnimeData.slug);
                const newLastWatchedData = await getLastWatched();
                setLastWatchedData(newLastWatchedData);
                showGenericCheckBoxNotification("Deleted from watch history!", `${getAnimeTitleByRelevance(selectedAnimeData.titles, false, language)} has been deleted from your watch history!`);
            },
            centered: true,
        });
    };

    const handleAddToWatchList = (e, selectedAnimeData) => {
        e.preventDefault();
        openConfirmModal({
            title: "Please confirm your action",
            children: <Text size="sm">Are you sure you want to add {getAnimeTitleByRelevance(selectedAnimeData.titles, false, language)} to your watchlist?</Text>,
            labels: { confirm: "Confirm", cancel: "Cancel" },
            confirmProps: { color: "red" },
            onCancel: () => {
                //dont do anything
            },
            onConfirm: async () => {
                await handleWatchListAdd(selectedAnimeData);
            },
            centered: true,
        });
    };

    return (
        <Transition mounted={isCardOpen} transition="slide-up" duration={300} timingFunction="linear">
            {(styles) => (
                <div style={{ ...styles }} className={classes.newCard}>
                    <Paper sx={{ width: "100%", height: "100%" }}>
                        <Paper sx={{ height: "calc(85% - 10px)", backgroundImage: `url(${getImageByRelevance(animeData.images, "large_image_url")})`, backgroundSize: "cover", borderRadius: "0px", backgroundPosition: "center" }}></Paper>
                        {animeData.playbackPercent ? (
                            <Paper className={classes.playBackTimeDiv}>
                                <Paper sx={{ width: `${animeData.playbackPercent * 0.9 ?? 0}%`, backgroundColor: WATCHANIME_RED, height: "100%", borderRadius: "0px" }}></Paper>
                                <Paper sx={{ width: `${100 - (animeData.playbackPercent * 0.9 ?? 0)}%`, backgroundColor: "gray", height: "100%", borderRadius: "0px" }}></Paper>
                            </Paper>
                        ) : (
                            <></>
                        )}
                        <Paper sx={{ height: "calc(15% - 10px)", paddingTop: "10px" }}>
                            <Text lineClamp={1} sx={{ fontSize: "16px", fontWeight: "600" }}>
                                {getAnimeTitleByRelevance(animeData.titles, false, language)}
                            </Text>
                            <Text lineClamp={1} sx={{ fontSize: "12px" }}>
                                {animeData.currentReleasedEpisode ? `EP ${animeData.currentReleasedEpisode}` : ""}
                                {animeData.currentReleasedEpisode ? " | " : ""}
                                {animeData.slug.includes("-dub") ? "DUB" : "SUB"}
                            </Text>
                        </Paper>

                        <Paper
                            sx={{
                                height: "100%",
                                width: "100%",
                                backgroundColor: "rgba(0,0,0,.95)",
                                borderRadius: "0px",
                                position: "absolute",
                                top: "0",
                                padding: "1rem",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                            }}
                            className={classes.animeCardHoverState}
                        >
                            <Paper sx={{ display: "flex", flexDirection: "column", backgroundColor: "transparent" }}>
                                <Text mr={ActionComponent ? "26px" : "0px"} fw={600}>
                                    {getAnimeTitleByRelevance(animeData.titles, false, language)}
                                </Text>
                                <Paper sx={{ display: "flex", alignItems: "center", backgroundColor: "transparent", gap: "5px", color: "grey" }} mt={"0.5rem"}>
                                    <Rating count={1} readOnly />
                                    <Text>{animeData.score}</Text>
                                </Paper>
                                <Text mt={"0.5rem"} sx={{ color: "grey" }}>
                                    {animeData.episodes ?? "NA"} Episodes
                                </Text>
                                <Text mt={"0.5rem"} lineClamp={8}>
                                    {animeData.synopsis}
                                </Text>
                            </Paper>
                            <Paper sx={{ display: "flex", alignItems: "center", backgroundColor: "transparent", gap: "3px" }}>
                                {isAddableToWatchList ? (
                                    <Paper sx={{ display: "flex", alignItems: "center", gap: "3px", backgroundColor: "transparent" }}>
                                        <Tooltip label="Add to WatchList" withArrow position="bottom" transition="scale" transitionDuration={100}>
                                            <IconPlus size={24} onClick={(e) => handleAddToWatchList(e, animeData)} />
                                        </Tooltip>
                                    </Paper>
                                ) : (
                                    <></>
                                )}
                                {isDeletable ? (
                                    <Tooltip label={featureId === "lastWatched" ? "Delete from Last Watched" : "Delete from WatchList"} withArrow position="bottom" transition="scale" transitionDuration={100}>
                                        <IconTrash size={24} onClick={(e) => handleDeleteFromAnimeCard(e, featureId, animeData)} />
                                    </Tooltip>
                                ) : (
                                    <></>
                                )}
                                {ActionComponent && <ActionComponent animeData={animeData} actionComponentData={actionComponentData} />}
                            </Paper>
                        </Paper>
                    </Paper>
                </div>
            )}
        </Transition>
    );
}

function AnimeSectionLayout({ anime, isDeletable, isAddableToWatchList, featureId, setLastWatchedData, actionComponent, actionComponentData }) {
    const { classes } = useStyles();
    return (
        <Anchor
            component={Link}
            to={`/anime/${anime.slug}${anime.currentReleasedEpisode ? `/episode/${anime.currentReleasedEpisode ? anime.currentReleasedEpisode : 0}` : ""}`}
            className={[classes.noTextDecoration, "anime-card-wrapper"]}
            sx={{ position: "relative" }}
        >
            <Card animeData={anime} isDeletable={isDeletable} featureId={featureId} isAddableToWatchList={isAddableToWatchList} setLastWatchedData={setLastWatchedData} ActionComponent={actionComponent} actionComponentData={actionComponentData} />
        </Anchor>
    );
}

export default AnimeSectionLayout;
